import {useAnnouncements} from "@/hooks/useAnnouncements";
import {DevLog} from "@buildwitt/shapeshift-common/v1/common";
import axios from "axios";
import {createContext, useReducer, ReactElement, Reducer, useEffect, Dispatch, FC, useContext} from "react";
import {AuthContext, INotificationSeverity, NotificationContext} from "@buildwitt/shapeshift-identity";
import {IAnnouncement} from "@/types/announcement";
import {LocaleContext} from "./localeContext";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import useDevCycleSdk from "@/hooks/useDevCycleSdk";
import {PageBaseContext} from "@/contexts/pageBaseContext";

const initialState: {announcementState: AnnouncementContextState; announcementDispatch: Dispatch<AnnouncementContextAction>} = {announcementState: undefined, announcementDispatch: () => {}};
const AnnouncementContext = createContext(initialState);

type AnnouncementContextState = IAnnouncement[] | undefined

type AnnouncementContextAction = {
	type: "set";
	payload?: IAnnouncement[];
} | {
	type: "setAnnouncementComplete",
	id: number,
}

interface IProps {
	children: ReactElement
}

const sortByPublishedAtDesc = (items?: IAnnouncement[]): IAnnouncement[] | undefined => {
	if (!items) return items;

	return [...items].sort((a, b) => {
		const aValue = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
		const bValue = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
		return bValue - aValue;
	});
};

const AnnouncementProvider: FC<IProps> = ({children}) => {
	const [{locale}] = useContext(LocaleContext);
	const {announcements, setAnnoucements, apiResult} = useAnnouncements(locale ?? "en")
	const {config, jwt} = useContext(AuthContext);
	const {notify} = useContext(NotificationContext);
	const {t} = useSSTranslation();
	const {announcements: announcementsFeatureFlag} = useDevCycleSdk();
	const [notificationCount, setPageBaseContext] = useContext(PageBaseContext);

	const [announcementState, announcementDispatch] = useReducer<Reducer<AnnouncementContextState, AnnouncementContextAction>>((state: AnnouncementContextState, action: AnnouncementContextAction) => {
		switch (action.type) {
			case "set": {
				return action.payload;
			}
			case "setAnnouncementComplete": {
				const current = (announcements ?? []).find(e => e.id === action.id);
				if(!current || !announcements) return state;

				current.complete = true;
				setAnnoucements([...announcements]);
				return state;
			}
			default:
				return state;
		}
	}, initialState.announcementState);

	//below is for debugging only
	useEffect(() => {
		if(announcementState)
			DevLog("UPDATE TO ANOUNCEMENT STATE", announcementState);
	}, [announcementState]);

	useEffect(() => {
		if(apiResult.isError) {
			notify(t("notifications_wrong_body", "Please try again"), t("notifications_wrong_title", "Something went wrong"), INotificationSeverity.error, 5000);
		}
	}, [apiResult])

	useEffect(() => {
		if(announcements) {
			announcementDispatch({type: "set", payload: sortByPublishedAtDesc(announcements)})
			if (announcementsFeatureFlag) {
				setPageBaseContext(prev => ({...prev, notificationCount: notificationCount.notificationCount + announcements.filter(a => !a.complete).length}));
			}
		}
	}, [announcements])

	// Direct axios call bypasses useApi's internal deep-equal cache, which prevents repeated GETs to the same endpoint.
	useEffect(() => {
		if (!announcementsFeatureFlag || !config?.baseApiUrl) return;
		const timezone = encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone);
		const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
		const parseDates = (obj: any): any =>
			JSON.parse(JSON.stringify(obj), (_k, v) =>
				typeof v === "string" && isoDateRegex.test(v) ? new Date(v.includes("Z") ? v : v + "Z") : v
			);
		const interval = setInterval(async () => {
			try {
				const headers: Record<string, string> = { Accept: "application/json" };
				if (jwt) headers.Authorization = `Bearer ${jwt}`;
				const { data } = await axios.get<IAnnouncement[]>(
					`${config.baseApiUrl}/api/announcement/learner?timezone=${timezone}&locale=${locale ?? "en"}`,
					{ headers, withCredentials: true }
				);
				if (data) {
					announcementDispatch({ type: "set", payload: sortByPublishedAtDesc(parseDates(data)) });
				}
			} catch { /* silently ignore polling errors */ }
		}, 60 * 1000);
		return () => clearInterval(interval);
	}, [announcementsFeatureFlag, config?.baseApiUrl, jwt, locale])

	return (
		<AnnouncementContext.Provider value={{announcementState, announcementDispatch}}>
			{children}
		</AnnouncementContext.Provider>
	);
};

export {
	AnnouncementContext,
	AnnouncementProvider
};